'use client';

import { FOCUS_RING } from '@common/components/interaction';
import { cn } from '@core/utils';

import type { UnauthApi } from '@/api';
import { PointCoin } from '@/app/_components/PointCoin';
import { Image } from '@/components/client';

import { resolveRewardRateLabel } from '../merchants/_utils/formatRewardRate';

export type GiftProductLink = NonNullable<
  NonNullable<
    Awaited<
      ReturnType<
        (typeof UnauthApi)['get:/wello/v1/allows/affiliate/gift-products/use-tab']
      >
    >['data']
  >['context']
>['contents'][number];

interface GiftProductLinkCardProps {
  product: GiftProductLink;
  onSelect: () => void;
}

export const GiftProductLinkCard = ({
  product,
  onSelect,
}: GiftProductLinkCardProps) => {
  const rewardRateLabel = resolveRewardRateLabel(product);
  const isPartnerPoint =
    product.rewardCurrencyType === 'AFFILIATE_PARTNER_POINT';

  return (
    <button
      onClick={onSelect}
      className={cn(
        'group flex flex-1 flex-col gap-6 rounded-large text-left',
        FOCUS_RING,
      )}
    >
      <div className="relative aspect-square w-full overflow-hidden rounded-large border border-Gray-black/5 bg-Gray-100">
        {product.thumbnailImageUrl ? (
          <Image
            src={product.thumbnailImageUrl}
            alt=""
            fill
            sizes="(min-width: 1280px) 130px, 45vw"
            className="object-cover"
          />
        ) : null}
        <span
          aria-hidden
          className="absolute inset-0 bg-Gray-black/0 transition-colors duration-150 group-hover:bg-Gray-black/[0.04] group-focus-visible:bg-Gray-black/[0.04] group-active:bg-Gray-black/[0.04] motion-reduce:transition-none"
        />
      </div>
      <div className="flex items-center gap-4">
        {product.logoImageUrl ? (
          <Image
            src={product.logoImageUrl}
            alt=""
            width={20}
            height={20}
            className="size-20 shrink-0 rounded-pill border border-Gray-300 object-contain"
          />
        ) : null}
        <span className="truncate font-600 text-Gray-600 typo-caption1">
          {product.merchantName}
        </span>
      </div>
      <p className="line-clamp-2 font-500 text-Gray-900 typo-body4">
        {product.productName}
      </p>
      {rewardRateLabel ? (
        <p
          className={cn(
            'flex items-center gap-4 font-600 typo-caption1',
            isPartnerPoint ? 'text-B-550' : 'text-Y-600',
          )}
        >
          <PointCoin
            currency={
              isPartnerPoint ? 'AFFILIATE_PARTNER_POINT' : 'WELLO_POINT'
            }
            size={17}
          />
          {rewardRateLabel} 적립
        </p>
      ) : null}
    </button>
  );
};
